/**
 * Bookable resources: bookings, occupancy, the queue and the ladder.
 *
 * Rooms, the ping pong table and the PlayStation are all "resources" and behave
 * identically (docs/01 §5). Four separate concerns live here because they all
 * hang off the same `resource_id`:
 *
 *  1. BOOKINGS are INTENT. Overlap is tested on the half-open interval
 *     `[startsAt, endsAt)` so a booking ending at 15:00 and one starting at
 *     15:00 do not collide. This is the same rule the Postgres
 *     `EXCLUDE USING gist (resource_id, tstzrange(starts_at, ends_at))`
 *     constraint enforces server-side (docs/02 §8), so the client must agree
 *     with it exactly or the outbox rejects writes the UI said were fine.
 *
 *  2. OCCUPANCY is REALITY. "In use now" writes occupancy and never touches
 *     bookings. At most one occupancy is open per resource at a time.
 *
 *  3. THE QUEUE (docs/01 §5.1) handles "I want it when whoever's on it
 *     finishes". Order is join time. The head gets a claim window; unclaimed at
 *     expiry it passes to the next person, which is the only thing stopping the
 *     queue stalling forever on someone who went home. A booking beats the
 *     queue: while a booked slot is running the queue pauses.
 *
 *  4. THE LADDER (docs/01 §5.2) is Elo, K = 32, starting at 1200, with
 *     provisional players moving their own rating faster and their opponents'
 *     less so one lucky guest cannot wreck the standings.
 *
 * Every function here is pure and takes time as an explicit `now` argument.
 * Nothing calls `Date.now()`; the tablet's clock is untrusted (docs/02 §4) and
 * two offline devices must compute byte-identical results with no coordination.
 * Nothing mutates its inputs: every "mutation" returns a new array.
 */

import type {
  BookingId,
  GuestId,
  MemberId,
  PersonId,
  ProposalId,
  QueueEntryId,
  ResourceId,
} from './ids.js'
import type {
  Booking,
  BookingKind,
  HeadToHead,
  Match,
  Occupancy,
  QueueEntry,
  QueueStatus,
  Rating,
  Resource,
} from './entities/resources.js'
import { DEFAULT_ROLLOVER_HOUR, rolloverDayBounds, type DayKey } from './time.js'

const MS_PER_MINUTE = 60_000

// ---------------------------------------------------------------------------
// Intervals — the half-open rule, in one place
// ---------------------------------------------------------------------------

/** A half-open span of UTC epoch milliseconds: `[startsAt, endsAt)`. */
export interface TimeInterval {
  readonly startsAt: number
  readonly endsAt: number
}

/** Duration in ms. Negative for a backwards interval; callers reject those. */
export const intervalDuration = (i: TimeInterval): number => i.endsAt - i.startsAt

/** Finite bounds and a strictly positive duration. Zero-length is not a booking. */
export function isValidInterval(i: TimeInterval): boolean {
  return Number.isFinite(i.startsAt) && Number.isFinite(i.endsAt) && i.endsAt > i.startsAt
}

/**
 * HALF-OPEN overlap: `[aStart, aEnd)` against `[bStart, bEnd)`.
 *
 * This is the load-bearing predicate of the whole module. `a.endsAt === b.startsAt`
 * is deliberately NOT an overlap — that is the back-to-back 15:00 case.
 *
 * An empty interval (zero or negative length) is the empty set and therefore
 * overlaps nothing, including itself. The bare `s1 < e2 && s2 < e1` form gets
 * this wrong: it reports `[t, t)` as overlapping any interval that strictly
 * contains `t`. Postgres `tstzrange` treats an empty range as overlapping
 * nothing, so without the guard the client would reject slots the server's
 * EXCLUDE constraint happily accepts (docs/02 §8) — the two must agree exactly.
 * Found by property test.
 */
export function intervalsOverlap(a: TimeInterval, b: TimeInterval): boolean {
  if (a.endsAt <= a.startsAt || b.endsAt <= b.startsAt) return false
  return a.startsAt < b.endsAt && b.startsAt < a.endsAt
}

/** Whether an instant falls inside `[startsAt, endsAt)`. */
export function intervalContains(i: TimeInterval, at: number): boolean {
  return at >= i.startsAt && at < i.endsAt
}

/** The overlapping part of two intervals, or null when they do not overlap. */
export function intervalIntersection(a: TimeInterval, b: TimeInterval): TimeInterval | null {
  const startsAt = Math.max(a.startsAt, b.startsAt)
  const endsAt = Math.min(a.endsAt, b.endsAt)
  return endsAt > startsAt ? { startsAt, endsAt } : null
}

/**
 * Sort and coalesce. Touching intervals merge: `[0,5)` and `[5,10)` become
 * `[0,10)`, because together they cover the same instants with no gap.
 * Intervals with a non-positive duration are dropped.
 */
export function mergeIntervals(intervals: readonly TimeInterval[]): TimeInterval[] {
  const sorted = intervals
    .filter(isValidInterval)
    .slice()
    .sort((a, b) => a.startsAt - b.startsAt || a.endsAt - b.endsAt)
  const out: TimeInterval[] = []
  for (const cur of sorted) {
    const last = out[out.length - 1]
    if (last !== undefined && cur.startsAt <= last.endsAt) {
      if (cur.endsAt > last.endsAt) out[out.length - 1] = { startsAt: last.startsAt, endsAt: cur.endsAt }
    } else {
      out.push({ startsAt: cur.startsAt, endsAt: cur.endsAt })
    }
  }
  return out
}

/** The parts of `window` not covered by `busy`. Complement of {@link mergeIntervals}. */
export function subtractIntervals(
  window: TimeInterval,
  busy: readonly TimeInterval[],
): TimeInterval[] {
  if (!isValidInterval(window)) return []
  const out: TimeInterval[] = []
  let cursor = window.startsAt
  for (const b of mergeIntervals(busy)) {
    const clipped = intervalIntersection(b, window)
    if (clipped === null) continue
    if (clipped.startsAt > cursor) out.push({ startsAt: cursor, endsAt: clipped.startsAt })
    cursor = Math.max(cursor, clipped.endsAt)
  }
  if (cursor < window.endsAt) out.push({ startsAt: cursor, endsAt: window.endsAt })
  return out
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

/**
 * A slot being tested for conflicts. A full {@link Booking} satisfies this, so
 * an existing booking can be re-checked (its own `id` is excluded from its own
 * conflict set, which is what makes "move this booking" work).
 */
export interface BookingCandidate {
  readonly id?: BookingId
  readonly resourceId: ResourceId
  readonly startsAt: number
  readonly endsAt: number
  readonly status?: Booking['status']
}

export type BookingRejection =
  | { readonly reason: 'non_finite_bounds' }
  | { readonly reason: 'non_positive_duration'; readonly durationMs: number }
  | { readonly reason: 'resource_not_bookable' }
  | { readonly reason: 'conflict'; readonly conflicts: readonly Booking[] }

export type BookingCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly rejection: BookingRejection }

/** Only `confirmed` bookings hold a slot. */
export const isConfirmed = (b: Booking): boolean => b.status === 'confirmed'

/**
 * Confirmed bookings on the same resource whose half-open interval overlaps the
 * candidate's.
 *
 * Cancelled bookings never conflict — that is the whole point of cancelling.
 * The candidate's own id is excluded so re-validating an existing booking does
 * not report it against itself. A zero-length or backwards candidate overlaps
 * nothing and returns `[]`; use {@link checkBooking} to have that rejected.
 *
 * Results are ordered by start time (then id) so two devices produce identical
 * conflict lists and therefore identical rejection messages.
 */
export function findConflicts(
  candidate: BookingCandidate,
  existing: readonly Booking[],
): Booking[] {
  return existing
    .filter(
      b =>
        isConfirmed(b) &&
        b.resourceId === candidate.resourceId &&
        b.id !== candidate.id &&
        intervalsOverlap(candidate, b),
    )
    .sort((x, y) => x.startsAt - y.startsAt || compareStrings(x.id, y.id))
}

export const hasConflict = (candidate: BookingCandidate, existing: readonly Booking[]): boolean =>
  findConflicts(candidate, existing).length > 0

/**
 * Full admission check: bounds, duration, resource bookability, conflicts.
 * `resource` is optional — pass it to enforce `bookable`/`active`.
 *
 * A candidate whose own status is `cancelled` skips the conflict test: a
 * cancelled booking occupies nothing.
 */
export function checkBooking(
  candidate: BookingCandidate,
  existing: readonly Booking[],
  resource?: Resource | null,
): BookingCheck {
  if (!Number.isFinite(candidate.startsAt) || !Number.isFinite(candidate.endsAt)) {
    return { ok: false, rejection: { reason: 'non_finite_bounds' } }
  }
  const durationMs = candidate.endsAt - candidate.startsAt
  if (durationMs <= 0) {
    return { ok: false, rejection: { reason: 'non_positive_duration', durationMs } }
  }
  if (resource != null && (!resource.bookable || !resource.active)) {
    return { ok: false, rejection: { reason: 'resource_not_bookable' } }
  }
  if (candidate.status === 'cancelled') return { ok: true }
  const conflicts = findConflicts(candidate, existing)
  if (conflicts.length > 0) return { ok: false, rejection: { reason: 'conflict', conflicts } }
  return { ok: true }
}

export interface NewBooking {
  readonly id: BookingId
  readonly resourceId: ResourceId
  readonly createdBy: MemberId
  readonly title: string
  readonly startsAt: number
  readonly endsAt: number
  readonly kind: BookingKind
  readonly createdAt: number
  readonly attendeeIds?: readonly MemberId[]
  readonly guestIds?: readonly GuestId[]
  readonly linkedProposalId?: ProposalId | null
}

/** Build a `confirmed` booking. Ids are supplied by the caller (uuidv7). */
export function createBooking(input: NewBooking): Booking {
  return {
    id: input.id,
    resourceId: input.resourceId,
    createdBy: input.createdBy,
    title: input.title,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    kind: input.kind,
    attendeeIds: [...(input.attendeeIds ?? [])],
    guestIds: [...(input.guestIds ?? [])],
    linkedProposalId: input.linkedProposalId ?? null,
    status: 'confirmed',
    createdAt: input.createdAt,
  }
}

export type BookingInsert =
  | { readonly ok: true; readonly bookings: Booking[]; readonly replayed: boolean }
  | { readonly ok: false; readonly rejection: BookingRejection; readonly bookings: Booking[] }

/**
 * Admit a booking into a set, or reject it.
 *
 * Replaying an id already present is a no-op that reports `ok` — the outbox
 * replays mutations after an offline period (docs/02 §8) and applying the same
 * booking twice must change nothing.
 */
export function insertBooking(
  existing: readonly Booking[],
  booking: Booking,
  resource?: Resource | null,
): BookingInsert {
  if (existing.some(b => b.id === booking.id)) {
    return { ok: true, bookings: existing.slice(), replayed: true }
  }
  const check = checkBooking(booking, existing, resource)
  if (!check.ok) return { ok: false, rejection: check.rejection, bookings: existing.slice() }
  return { ok: true, bookings: [...existing, booking], replayed: false }
}

/** Cancel by id. Idempotent; unknown ids are left alone. */
export function cancelBooking(bookings: readonly Booking[], id: BookingId): Booking[] {
  return bookings.map(b => (b.id === id && b.status !== 'cancelled' ? { ...b, status: 'cancelled' as const } : b))
}

export interface BookingAdmission {
  readonly accepted: Booking[]
  readonly rejected: ReadonlyArray<{ readonly booking: Booking; readonly rejection: BookingRejection }>
}

/**
 * Greedily admit candidates in the order given, which is how the server's
 * per-mutation savepoint loop behaves (docs/02 §8): earlier writes win, later
 * conflicting ones are rejected individually rather than aborting the batch.
 */
export function acceptBookings(
  candidates: readonly Booking[],
  existing: readonly Booking[] = [],
): BookingAdmission {
  let accepted: Booking[] = existing.slice()
  const rejected: Array<{ booking: Booking; rejection: BookingRejection }> = []
  for (const c of candidates) {
    const r = insertBooking(accepted, c)
    if (r.ok) accepted = r.bookings
    else rejected.push({ booking: c, rejection: r.rejection })
  }
  return { accepted, rejected }
}

/** Confirmed bookings on a resource, ordered by start time then id. */
export function confirmedBookings(
  bookings: readonly Booking[],
  resourceId: ResourceId,
): Booking[] {
  return bookings
    .filter(b => b.resourceId === resourceId && isConfirmed(b))
    .sort((a, b) => a.startsAt - b.startsAt || compareStrings(a.id, b.id))
}

/**
 * The confirmed booking running at `now`, or null.
 *
 * At most one can exist once {@link checkBooking} has been enforced; if dirty
 * synced data contains more, the earliest-starting one wins so every device
 * picks the same answer.
 *
 * A booking whose interval is not finite is not running, ever. `intervalContains`
 * alone says a `[start, +Infinity)` row is live at every instant from now to the
 * heat death of the office, which would pause the queue permanently — while
 * {@link busyIntervals} drops the same row, so nothing would freeze the claim
 * window to compensate and `pausedUntil` would render as `Infinity` on the wall.
 * The two views of "booked" have to agree, so both refuse it.
 */
export function activeBookingAt(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  now: number,
): Booking | null {
  return (
    confirmedBookings(bookings, resourceId).find(
      b => isValidInterval(b) && intervalContains(b, now),
    ) ?? null
  )
}

/** The next confirmed booking starting at or after `now`. */
export function nextBookingAfter(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  now: number,
): Booking | null {
  return confirmedBookings(bookings, resourceId).find(b => b.startsAt >= now) ?? null
}

/** Confirmed bookings on a resource overlapping a window, ordered by start. */
export function bookingsInRange(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  window: TimeInterval,
): Booking[] {
  return confirmedBookings(bookings, resourceId).filter(b => intervalsOverlap(b, window))
}

/**
 * Confirmed bookings falling in an office day — the 05:00-to-05:00 reckoning,
 * not midnight (docs/01 cross-cutting rule). A 21:00-to-01:00 booking belongs
 * to one day, and the two days a year Cairo changes offset are 23 or 25 hours
 * long; `rolloverDayBounds` already handles both.
 */
export function bookingsOnDay(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  day: DayKey,
  rolloverHour: number = DEFAULT_ROLLOVER_HOUR,
): Booking[] {
  const { startUtc, endUtc } = rolloverDayBounds(day, rolloverHour)
  return bookingsInRange(bookings, resourceId, { startsAt: startUtc, endsAt: endUtc })
}

/** Merged busy spans from confirmed bookings, clipped to `window` if given. */
export function busyIntervals(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  window?: TimeInterval,
): TimeInterval[] {
  const raw = confirmedBookings(bookings, resourceId).map(b => ({
    startsAt: b.startsAt,
    endsAt: b.endsAt,
  }))
  const merged = mergeIntervals(raw)
  if (window === undefined) return merged
  return merged
    .map(i => intervalIntersection(i, window))
    .filter((i): i is TimeInterval => i !== null)
}

/** The bookable gaps inside `window`. Complement of {@link busyIntervals}. */
export function freeSlots(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  window: TimeInterval,
): TimeInterval[] {
  return subtractIntervals(window, busyIntervals(bookings, resourceId))
}

// ---------------------------------------------------------------------------
// Occupancy — reality, as opposed to intent
// ---------------------------------------------------------------------------

export type OccupancySource = Occupancy['source']

export interface OccupancyStart {
  readonly id: string
  readonly resourceId: ResourceId
  readonly startedBy: PersonId
  readonly startedAt: number
  readonly source?: OccupancySource
}

/** The open (un-ended) occupancy on a resource, latest start wins. */
export function openOccupancy(
  occupancies: readonly Occupancy[],
  resourceId: ResourceId,
): Occupancy | null {
  const open = occupancies
    .filter(o => o.resourceId === resourceId && o.endedAt === null)
    .sort((a, b) => b.startedAt - a.startedAt || compareStrings(b.id, a.id))
  return open[0] ?? null
}

export const isInUse = (occupancies: readonly Occupancy[], resourceId: ResourceId): boolean =>
  openOccupancy(occupancies, resourceId) !== null

/**
 * Start "in use now".
 *
 * Only one occupancy is open per resource at a time, so starting a new one
 * closes whatever was open. The old session's `endedAt` is clamped to at least
 * its own `startedAt`, because a hand-set tablet clock can hand us a `now`
 * earlier than the session it is closing and a negative-duration session would
 * poison every usage total downstream.
 *
 * Replaying an id already present is a no-op. A non-finite `startedAt` is
 * refused: `Math.max` cannot clamp a NaN, so it would end up in `endedAt` on the
 * session this one closes and poison every usage total downstream.
 */
export function startOccupancy(
  occupancies: readonly Occupancy[],
  start: OccupancyStart,
): Occupancy[] {
  if (!Number.isFinite(start.startedAt)) return occupancies.slice()
  if (occupancies.some(o => o.id === start.id)) return occupancies.slice()
  const closed = occupancies.map(o =>
    o.resourceId === start.resourceId && o.endedAt === null
      ? { ...o, endedAt: Math.max(o.startedAt, start.startedAt) }
      : o,
  )
  return [
    ...closed,
    {
      id: start.id,
      resourceId: start.resourceId,
      startedBy: start.startedBy,
      startedAt: start.startedAt,
      endedAt: null,
      source: start.source ?? 'manual',
    },
  ]
}

/**
 * End whatever is open on a resource. Idempotent when nothing is open.
 *
 * An untrusted clock leaves the session open rather than closing it with a NaN:
 * a session that still reads "in use" is a visible, correctable wrong state; one
 * with a NaN `endedAt` reads as closed and silently poisons every total.
 */
export function endOccupancy(
  occupancies: readonly Occupancy[],
  resourceId: ResourceId,
  now: number,
): Occupancy[] {
  if (!Number.isFinite(now)) return occupancies.slice()
  return occupancies.map(o =>
    o.resourceId === resourceId && o.endedAt === null
      ? { ...o, endedAt: Math.max(o.startedAt, now) }
      : o,
  )
}

/** End one specific occupancy. Idempotent; already-ended sessions are untouched. */
export function endOccupancyById(
  occupancies: readonly Occupancy[],
  id: string,
  now: number,
): Occupancy[] {
  if (!Number.isFinite(now)) return occupancies.slice()
  return occupancies.map(o =>
    o.id === id && o.endedAt === null ? { ...o, endedAt: Math.max(o.startedAt, now) } : o,
  )
}

/**
 * Elapsed ms of an occupancy; open sessions are measured to `now`.
 * Never negative, and never `NaN` — an untrusted clock reads as zero rather than
 * putting `NaN` on the wall.
 */
export function occupancyDurationMs(o: Occupancy, now: number): number {
  const end = o.endedAt ?? now
  if (!Number.isFinite(end) || !Number.isFinite(o.startedAt)) return 0
  return Math.max(0, end - o.startedAt)
}

/** Total occupied ms inside a window — the room-usage figure for the recap. */
export function totalOccupancyMs(
  occupancies: readonly Occupancy[],
  resourceId: ResourceId,
  window: TimeInterval,
  now: number,
): number {
  const spans = occupancies
    .filter(o => o.resourceId === resourceId)
    .map(o => ({ startsAt: o.startedAt, endsAt: Math.max(o.startedAt, o.endedAt ?? now) }))
  return mergeIntervals(spans)
    .map(i => intervalIntersection(i, window))
    .filter((i): i is TimeInterval => i !== null)
    .reduce((sum, i) => sum + intervalDuration(i), 0)
}

// ---------------------------------------------------------------------------
// The queue (docs/01 §5.1)
// ---------------------------------------------------------------------------

export const DEFAULT_CLAIM_WINDOW_MINUTES = 5

/** `waiting`, `claimed` and `playing` are live; `expired` and `left` are terminal. */
export const isActiveQueueStatus = (s: QueueStatus): boolean =>
  s === 'waiting' || s === 'claimed' || s === 'playing'

export const isTerminalQueueStatus = (s: QueueStatus): boolean => !isActiveQueueStatus(s)

/**
 * Order is join time (docs/01 §5.1), with the entry id as tiebreak. Ids are
 * UUIDv7 so the tiebreak is itself time-ordered, and — critically — it is a
 * total order every device computes identically without a server counter.
 */
export function compareQueueEntries(a: QueueEntry, b: QueueEntry): number {
  return a.joinedAt - b.joinedAt || compareStrings(a.id, b.id)
}

/** Live entries on a resource, in queue order. */
export function queueFor(entries: readonly QueueEntry[], resourceId: ResourceId): QueueEntry[] {
  return entries
    .filter(e => e.resourceId === resourceId && isActiveQueueStatus(e.status))
    .sort(compareQueueEntries)
}

/** Entries still waiting for their turn, in queue order. */
export const waitingFor = (entries: readonly QueueEntry[], resourceId: ResourceId): QueueEntry[] =>
  queueFor(entries, resourceId).filter(e => e.status === 'waiting')

export interface NewQueueEntry {
  readonly id: QueueEntryId
  readonly resourceId: ResourceId
  readonly personId: PersonId
  readonly joinedAt: number
}

/**
 * Join the queue.
 *
 * Idempotent on entry id (outbox replay) and idempotent per person: someone
 * already waiting, claiming or playing on the resource cannot join twice and
 * jump their own place.
 *
 * A non-finite `joinedAt` is refused rather than stored: queue order is join
 * time, and an unorderable entry is worse than a missing one.
 */
export function joinQueue(entries: readonly QueueEntry[], entry: NewQueueEntry): QueueEntry[] {
  if (!Number.isFinite(entry.joinedAt)) return entries.slice()
  if (entries.some(e => e.id === entry.id)) return entries.slice()
  const alreadyIn = entries.some(
    e =>
      e.resourceId === entry.resourceId &&
      e.personId === entry.personId &&
      isActiveQueueStatus(e.status),
  )
  if (alreadyIn) return entries.slice()
  return [
    ...entries,
    {
      id: entry.id,
      resourceId: entry.resourceId,
      personId: entry.personId,
      joinedAt: entry.joinedAt,
      status: 'waiting',
      claimWindowEndsAt: null,
      resolvedAt: null,
    },
  ]
}

/**
 * Leave the queue. Idempotent: terminal entries are untouched.
 *
 * An untrusted clock is refused: every terminal entry must carry a real
 * `resolvedAt`, and `NaN` satisfies "not null" while meaning nothing.
 */
export function leaveQueue(
  entries: readonly QueueEntry[],
  entryId: QueueEntryId,
  now: number,
): QueueEntry[] {
  if (!Number.isFinite(now)) return entries.slice()
  return entries.map(e =>
    e.id === entryId && isActiveQueueStatus(e.status)
      ? { ...e, status: 'left' as const, resolvedAt: now }
      : e,
  )
}

/** Leave by person rather than entry id — what the tablet's "cancel" button has. */
export function leaveQueueByPerson(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  personId: PersonId,
  now: number,
): QueueEntry[] {
  if (!Number.isFinite(now)) return entries.slice()
  return entries.map(e =>
    e.resourceId === resourceId && e.personId === personId && isActiveQueueStatus(e.status)
      ? { ...e, status: 'left' as const, resolvedAt: now }
      : e,
  )
}

/**
 * The instant `durationMs` of *unbooked* time after `from` runs out.
 *
 * Used to size a claim window at the moment it is issued, so the five minutes
 * §5.1 promises are five minutes in which the claimer could actually have taken
 * the table. Booked time is skipped rather than counted. The result never lands
 * inside a known booking, which is why the read-side freeze below cannot
 * double-count the same slot.
 */
function advanceOverBusy(
  from: number,
  durationMs: number,
  busy: readonly TimeInterval[],
): number {
  let cursor = from
  let remaining = durationMs
  for (const b of busy) {
    if (b.endsAt <= cursor) continue
    if (b.startsAt >= cursor + remaining) break
    remaining -= Math.max(0, b.startsAt - cursor)
    cursor = b.endsAt
  }
  return cursor + remaining
}

/**
 * Where a claim window really ends, given that a booking freezes it.
 *
 * "A booking beats the queue: the queue pauses" (docs/01 §5.1). Pausing only
 * *promotion* is not enough: a window issued at 13:59 for five minutes, with a
 * booking running 14:00-15:00, is entirely consumed by a slot in which the
 * claimer physically cannot take the table. Suppressing the expiry while the
 * booking runs merely defers it — the sweep at 15:00 finds the window long gone
 * and expires them at the exact instant they become able to play, which is the
 * punishment the pause exists to prevent.
 *
 * So the window does not *tick* while the resource is booked: a window whose end
 * falls anywhere in the CLOSED span `[bookingStart, bookingEnd]` is pushed past
 * the booking, carrying the part of itself the booking swallowed
 * (`endsAt - bookingStart`) to the far side. Repeated for each booking the
 * pushed end lands in, so a run of back-to-back bookings freezes it once.
 *
 * The right-hand end of that span is closed, not half-open, and that is the
 * whole bug this function exists to prevent. A window ending at *exactly* the
 * booking's end has had every one of its remaining instants eaten by the slot —
 * `[start, end)` covers them all — so it is the worst case, not the safe one.
 * Testing `intervalContains` (half-open) skipped it, and the claimer was expired
 * at precisely `bookingEnd`: the instant the table freed up, with not one
 * instant in which the tap would have worked. Promoted at 15:00 with a five
 * minute window, then a 15:00-15:05 booking syncs in from a phone, and the
 * queue moved on without him. Found by property test; see the regression test.
 *
 * Raw confirmed bookings, not `busyIntervals`, because merging is what makes the
 * carry over-pay: a window already sized around 15:00-16:00 ends at 16:04, and a
 * back-to-back 16:00-16:04 booking merges the pair into 15:00-16:04, so the
 * carry hands back the whole 64 minutes instead of the 4 that were owed.
 * Un-merged, each booking gives back only what it actually took. Confirmed
 * bookings on one resource cannot overlap (docs/02 §8 — Postgres enforces it),
 * so the un-merged list is already disjoint; the outer loop only exists so that
 * dirty synced data which *does* overlap still reaches a fixpoint. Each booking
 * can fire at most once — a push always leaves `endsAt` strictly past that
 * booking's end — so the loop is bounded by the number of bookings.
 *
 * This is the read-side half. {@link promoteQueue} already sizes the window over
 * unbooked time when it is issued, which is exact; this catches the booking that
 * was created — or synced in from a phone — *after* the promotion, which the
 * write-side pass could not have known about. `advanceOverBusy` never lands on
 * or inside a booking it knew about, so the two never compound.
 *
 * The result is a function of stored data alone — never of `now` — so two
 * devices sweeping at different moments agree on it exactly.
 */
export function effectiveClaimWindowEnd(
  claimWindowEndsAt: number,
  resourceId: ResourceId,
  bookings?: readonly Booking[],
): number {
  if (bookings === undefined || !Number.isFinite(claimWindowEndsAt)) return claimWindowEndsAt
  // Non-finite or backwards bookings are dropped, exactly as `busyIntervals`
  // drops them, so a corrupt row can never push a window to infinity.
  const slots = confirmedBookings(bookings, resourceId).filter(isValidInterval)
  let endsAt = claimWindowEndsAt
  // Each slot may hand back its own minutes once and once only. Without that,
  // a window ending exactly at a slot's START carries nothing across it, lands
  // exactly on the slot's END, and the same slot pays out a second time.
  const paid = new Set<number>()
  for (let pass = 0; pass <= slots.length; pass++) {
    let moved = false
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i]!
      if (paid.has(i)) continue
      if (endsAt >= slot.startsAt && endsAt <= slot.endsAt) {
        endsAt = slot.endsAt + (endsAt - slot.startsAt)
        paid.add(i)
        moved = true
      }
    }
    if (!moved) break
  }
  return endsAt
}

/**
 * Expire claim windows that have lapsed.
 *
 * `resolvedAt` is set to the window's own end, never to `now`. Two devices
 * sweeping at different moments then agree on the resulting record exactly,
 * which they would not if the sweep time leaked into the data.
 *
 * Pass `bookings` to have windows frozen across booked slots
 * (see {@link effectiveClaimWindowEnd}); `resolvedAt` is then the frozen end,
 * which is still pure stored data.
 *
 * A non-finite `now` is an untrusted clock (docs/02 §4) and sweeps nothing —
 * `NaN >= x` is false anyway, but saying so is cheaper than relying on it.
 */
export function expireLapsedClaims(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  now: number,
  bookings?: readonly Booking[],
): QueueEntry[] {
  if (!Number.isFinite(now)) return entries.slice()
  return entries.map(e => {
    if (e.resourceId !== resourceId || e.status !== 'claimed' || e.claimWindowEndsAt === null) {
      return e
    }
    const endsAt = effectiveClaimWindowEnd(e.claimWindowEndsAt, resourceId, bookings)
    return now >= endsAt ? { ...e, status: 'expired' as const, resolvedAt: endsAt } : e
  })
}

export interface QueueContext {
  /** Used to detect the booking that pauses the queue. */
  readonly bookings?: readonly Booking[]
  /** Used to detect that the resource is still physically in use. */
  readonly occupancies?: readonly Occupancy[]
}

/** A confirmed booking running right now beats the queue (docs/01 §5.1). */
export function isQueuePaused(
  bookings: readonly Booking[],
  resourceId: ResourceId,
  now: number,
): boolean {
  return activeBookingAt(bookings, resourceId, now) !== null
}

/**
 * Advance the queue: expire lapsed claim windows, then hand the head a fresh one.
 *
 * Called whenever the current occupancy ends and on a periodic sweep. The rules,
 * in order:
 *
 *  - **A booking pauses everything.** While a confirmed booking is running on
 *    the resource nothing is promoted and nothing expires — expiring someone's
 *    window while they are physically unable to claim would punish them for the
 *    booking. The queue resumes untouched when the slot ends, and the window
 *    itself was frozen across the slot ({@link effectiveClaimWindowEnd}), so the
 *    claimer gets their unused minutes back rather than being expired the
 *    instant the table frees up.
 *  - **Lapsed claims expire**, and pass to the next person. This is the rule
 *    that stops the queue stalling forever on someone who went home.
 *  - **Nothing is promoted while the resource is occupied** or while an entry is
 *    already `playing` or holding an unexpired claim. A synced `claimed` row
 *    carrying no window at all is given one, since a window that can never lapse
 *    is a queue that can never drain.
 *  - Otherwise the head of `waiting` — earliest `joinedAt` — becomes `claimed`
 *    with a window of `claimWindowMinutes` from `now`.
 *
 * At most one promotion happens per call, so a sweep loop drains the queue one
 * lapsed head at a time and never skips anybody.
 */
export function promoteQueue(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  now: number,
  claimWindowMinutes: number = DEFAULT_CLAIM_WINDOW_MINUTES,
  context?: QueueContext,
): QueueEntry[] {
  // An untrusted clock (docs/02 §4) must never reach stored data: `now + minutes`
  // would write a NaN claim window, and a NaN window never lapses, which is
  // exactly the silent stall §5.1 invents the claim window to prevent.
  if (!Number.isFinite(now)) return entries.slice()

  const bookings = context?.bookings
  if (bookings !== undefined && isQueuePaused(bookings, resourceId, now)) return entries.slice()

  const swept = expireLapsedClaims(entries, resourceId, now, bookings)

  const occupancies = context?.occupancies
  if (occupancies !== undefined && isInUse(occupancies, resourceId)) return swept

  const live = swept.filter(e => e.resourceId === resourceId && isActiveQueueStatus(e.status))
  if (live.some(e => e.status === 'playing')) return swept

  const minutes = Number.isFinite(claimWindowMinutes)
    ? Math.max(0, claimWindowMinutes)
    : DEFAULT_CLAIM_WINDOW_MINUTES
  // The window is five minutes of time in which they could actually take the
  // table, so a booking starting inside it is skipped rather than counted.
  const endsAt =
    bookings === undefined
      ? now + minutes * MS_PER_MINUTE
      : advanceOverBusy(now, minutes * MS_PER_MINUTE, busyIntervals(bookings, resourceId))

  const holders = live.filter(e => e.status === 'claimed')
  if (holders.length > 0) {
    // A `claimed` row carrying no window cannot lapse, so the queue would stall
    // on it forever — the one failure §5.1 introduces the claim window to
    // prevent. Only dirty synced data can produce one (this function always
    // writes a window), so heal it into a normal window rather than leaving a
    // dead queue. A holder with a live window is simply left to run it down.
    const dangling = new Set(holders.filter(e => e.claimWindowEndsAt === null).map(e => e.id))
    if (dangling.size === 0) return swept
    return swept.map(e => (dangling.has(e.id) ? { ...e, claimWindowEndsAt: endsAt } : e))
  }

  const head = live.filter(e => e.status === 'waiting').sort(compareQueueEntries)[0]
  if (head === undefined) return swept

  return swept.map(e =>
    e.id === head.id ? { ...e, status: 'claimed' as const, claimWindowEndsAt: endsAt } : e,
  )
}

/**
 * Claim your slot. Inside the window this becomes `playing`; at or past the
 * window end it expires instead, so a late tap cannot steal a turn that has
 * already passed on. Idempotent for an entry that is already `playing`.
 *
 * Pass the same `context` {@link promoteQueue} is given, so the window a booking
 * froze is judged by its frozen end. Without it the pause leaks: the sweep would
 * spare the claimer while their own tap expired them. With it, a tap while a
 * booking is running is a no-op — the queue is paused, so the turn cannot start
 * yet, and the frozen window is still there when the slot ends.
 */
export function claimQueueSlot(
  entries: readonly QueueEntry[],
  entryId: QueueEntryId,
  now: number,
  context?: QueueContext,
): QueueEntry[] {
  if (!Number.isFinite(now)) return entries.slice()
  const entry = entries.find(e => e.id === entryId)
  if (entry === undefined || entry.status !== 'claimed') return entries.slice()
  const bookings = context?.bookings
  if (bookings !== undefined && isQueuePaused(bookings, entry.resourceId, now)) {
    return entries.slice()
  }
  if (entry.claimWindowEndsAt !== null) {
    const endsAt = effectiveClaimWindowEnd(entry.claimWindowEndsAt, entry.resourceId, bookings)
    if (now >= endsAt) {
      return entries.map(e =>
        e.id === entryId ? { ...e, status: 'expired' as const, resolvedAt: endsAt } : e,
      )
    }
  }
  return entries.map(e => (e.id === entryId ? { ...e, status: 'playing' as const } : e))
}

/**
 * Finish the turn of whoever is on the resource.
 *
 * `QueueEntry` has no `done` state, so a completed turn lands in `left` — the
 * terminal "no longer in the queue" bucket — with `resolvedAt` recording when.
 * Pair this with {@link endOccupancy} and then {@link promoteQueue}.
 */
export function endTurn(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  now: number,
): QueueEntry[] {
  if (!Number.isFinite(now)) return entries.slice()
  return entries.map(e =>
    e.resourceId === resourceId && e.status === 'playing'
      ? { ...e, status: 'left' as const, resolvedAt: now }
      : e,
  )
}

/** 1-based place in the waiting line, or null if not waiting. */
export function queuePosition(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  personId: PersonId,
): number | null {
  const idx = waitingFor(entries, resourceId).findIndex(e => e.personId === personId)
  return idx === -1 ? null : idx + 1
}

export interface QueueSnapshot {
  readonly resourceId: ResourceId
  /** "on now: Omar" */
  readonly onNow: PersonId | null
  /** The head, notified and inside their claim window. */
  readonly claiming: {
    readonly entryId: QueueEntryId
    readonly personId: PersonId
    readonly expiresAt: number | null
  } | null
  /** "next: Ali, Youssef" */
  readonly waiting: PersonId[]
  readonly paused: boolean
  /** When the booking that paused the queue ends. */
  readonly pausedUntil: number | null
}

/**
 * Everything the wall display needs, in one pure read.
 *
 * A claim whose window has already lapsed is NOT reported as claiming: the wall
 * would otherwise show "next: Omar" with a countdown that ran out an hour ago
 * until some device happened to sweep. The snapshot shows what the next
 * {@link promoteQueue} at this same `now` would leave behind — which is why a
 * lapsed window is still honoured while a booking has the queue paused, exactly
 * as the sweep would honour it.
 *
 * `expiresAt` is the frozen end ({@link effectiveClaimWindowEnd}) so the
 * countdown on the wall matches the deadline actually enforced.
 */
export function queueSnapshot(
  entries: readonly QueueEntry[],
  resourceId: ResourceId,
  now: number,
  context?: QueueContext,
): QueueSnapshot {
  const bookings = context?.bookings
  const blocking = bookings === undefined ? null : activeBookingAt(bookings, resourceId, now)
  const live = queueFor(entries, resourceId)
  const playing = live.find(e => e.status === 'playing') ?? null
  const claimed = live.find(e => e.status === 'claimed') ?? null
  const expiresAt =
    claimed === null || claimed.claimWindowEndsAt === null
      ? null
      : effectiveClaimWindowEnd(claimed.claimWindowEndsAt, resourceId, bookings)
  const lapsed =
    blocking === null && expiresAt !== null && Number.isFinite(now) && now >= expiresAt
  return {
    resourceId,
    onNow: playing?.personId ?? null,
    claiming:
      claimed === null || lapsed
        ? null
        : { entryId: claimed.id, personId: claimed.personId, expiresAt },
    waiting: live.filter(e => e.status === 'waiting').map(e => e.personId),
    paused: blocking !== null,
    pausedUntil: blocking?.endsAt ?? null,
  }
}

// ---------------------------------------------------------------------------
// The ladder (docs/01 §5.2) — Elo
// ---------------------------------------------------------------------------

export const STARTING_RATING = 1200
/** K = 32 for an established player against an established opponent. */
export const K_BASE = 32
/** A provisional player's own rating moves twice as fast. */
export const K_PROVISIONAL = 64
/** An established player's rating moves half as much against a provisional one. */
export const K_VS_PROVISIONAL = 16
/** Fewer than this many matches played and you are still provisional. */
export const PROVISIONAL_MATCH_THRESHOLD = 5

export interface LadderOptions {
  readonly startingRating?: number
  readonly kBase?: number
  readonly kProvisional?: number
  readonly kVsProvisional?: number
  readonly provisionalThreshold?: number
}

interface ResolvedLadderOptions {
  readonly startingRating: number
  readonly kBase: number
  readonly kProvisional: number
  readonly kVsProvisional: number
  readonly provisionalThreshold: number
}

export const DEFAULT_LADDER_OPTIONS: ResolvedLadderOptions = {
  startingRating: STARTING_RATING,
  kBase: K_BASE,
  kProvisional: K_PROVISIONAL,
  kVsProvisional: K_VS_PROVISIONAL,
  provisionalThreshold: PROVISIONAL_MATCH_THRESHOLD,
}

function resolveOptions(o?: LadderOptions): ResolvedLadderOptions {
  if (o === undefined) return DEFAULT_LADDER_OPTIONS
  return {
    startingRating: o.startingRating ?? DEFAULT_LADDER_OPTIONS.startingRating,
    kBase: o.kBase ?? DEFAULT_LADDER_OPTIONS.kBase,
    kProvisional: o.kProvisional ?? DEFAULT_LADDER_OPTIONS.kProvisional,
    kVsProvisional: o.kVsProvisional ?? DEFAULT_LADDER_OPTIONS.kVsProvisional,
    provisionalThreshold: o.provisionalThreshold ?? DEFAULT_LADDER_OPTIONS.provisionalThreshold,
  }
}

/**
 * Standard Elo expectation: `1 / (1 + 10^((ratingB - ratingA) / 400))`.
 * Equal ratings give exactly 0.5; a 400-point lead gives ~0.909.
 */
export function expectedScore(ratingA: number, ratingB: number): number {
  return 1 / (1 + 10 ** ((ratingB - ratingA) / 400))
}

/**
 * Snap a rating to a 1e-6 grid, symmetrically about zero.
 *
 * `Math.pow` is not bit-identical across JS engines, so two devices could
 * otherwise drift in the last ULP and never reconverge — the ratings are stored
 * data, not a recomputed view. Quantising kills that divergence, and doing it
 * symmetrically (`q(-x) === -q(x)` exactly) keeps singles Elo zero-sum.
 */
export function quantizeRating(x: number): number {
  if (!Number.isFinite(x)) return 0
  const s = x < 0 ? -1 : 1
  return (s * Math.round(Math.abs(x) * 1e6)) / 1e6
}

export type MatchRejection =
  | 'empty_side'
  | 'duplicate_player'
  | 'player_on_both_sides'
  | 'side_size_mismatch'
  | 'unknown_winner'

/**
 * Structural validity. Singles is 1v1 and doubles is 2v2; anything else is a
 * data-entry error rather than a format we quietly support.
 */
export function validateMatch(match: Match): MatchRejection | null {
  if (match.sideA.length === 0 || match.sideB.length === 0) return 'empty_side'
  if (new Set(match.sideA).size !== match.sideA.length) return 'duplicate_player'
  if (new Set(match.sideB).size !== match.sideB.length) return 'duplicate_player'
  if (match.sideA.some(p => match.sideB.includes(p))) return 'player_on_both_sides'
  const expected = match.kind === 'singles' ? 1 : 2
  if (match.sideA.length !== expected || match.sideB.length !== expected) {
    return 'side_size_mismatch'
  }
  if (match.winner !== 'a' && match.winner !== 'b') return 'unknown_winner'
  return null
}

export const isValidMatch = (match: Match): boolean => validateMatch(match) === null

/** A fresh unrated player. */
export function newRating(
  personId: PersonId,
  resourceId: ResourceId,
  opts?: LadderOptions,
): Rating {
  return {
    personId,
    resourceId,
    rating: resolveOptions(opts).startingRating,
    matchesPlayed: 0,
    provisional: true,
    wins: 0,
    losses: 0,
    currentStreak: 0,
    bestStreak: 0,
  }
}

const ratingKey = (personId: PersonId, resourceId: ResourceId): string =>
  `${personId}\u0000${resourceId}`

/** Existing rating, or a fresh one at the starting rating. Never null. */
export function getRating(
  ratings: readonly Rating[],
  personId: PersonId,
  resourceId: ResourceId,
  opts?: LadderOptions,
): Rating {
  return (
    ratings.find(r => r.personId === personId && r.resourceId === resourceId) ??
    newRating(personId, resourceId, opts)
  )
}

export interface RatingChange {
  readonly personId: PersonId
  readonly side: 'a' | 'b'
  readonly won: boolean
  readonly kFactor: number
  readonly before: number
  readonly delta: number
  readonly after: number
}

/**
 * The per-player rating deltas a match produces, without applying them.
 *
 * Doubles averages each side's rating to compute the expectation, then applies
 * the same delta to both members of that side (docs/01 §5.2).
 *
 * K selection, which is the anti-guest rule:
 *   - provisional yourself      -> `kProvisional` (your rating moves fast)
 *   - established, opponent provisional -> `kVsProvisional` (you move less)
 *   - both established          -> `kBase`
 * A side counts as provisional if *any* of its members is, so a guest partnered
 * with a regular still damps the opposition's swing.
 *
 * `expectedB` is computed as `1 - expectedA` rather than independently, which
 * makes the two deltas exact negations of each other in floating point — that is
 * what lets singles Elo be zero-sum to the bit when both K factors match.
 */
export function matchRatingChanges(
  ratings: readonly Rating[],
  match: Match,
  opts?: LadderOptions,
): RatingChange[] {
  if (!isValidMatch(match)) return []
  const o = resolveOptions(opts)
  const before = new Map<PersonId, Rating>()
  for (const p of [...match.sideA, ...match.sideB]) {
    before.set(p, getRating(ratings, p, match.resourceId, opts))
  }

  const mean = (side: readonly PersonId[]): number =>
    side.reduce((s, p) => s + (before.get(p)?.rating ?? o.startingRating), 0) / side.length

  const expectedA = expectedScore(mean(match.sideA), mean(match.sideB))
  const expectedB = 1 - expectedA
  const scoreA = match.winner === 'a' ? 1 : 0
  const scoreB = 1 - scoreA

  const sideProvisional = (side: readonly PersonId[]): boolean =>
    side.some(p => before.get(p)?.provisional ?? true)
  const aProv = sideProvisional(match.sideA)
  const bProv = sideProvisional(match.sideB)

  const kFor = (selfProvisional: boolean, opponentProvisional: boolean): number =>
    selfProvisional ? o.kProvisional : opponentProvisional ? o.kVsProvisional : o.kBase

  const build = (
    side: 'a' | 'b',
    members: readonly PersonId[],
    score: number,
    expected: number,
    selfProv: boolean,
    oppProv: boolean,
  ): RatingChange[] => {
    const k = kFor(selfProv, oppProv)
    const delta = quantizeRating(k * (score - expected))
    return members.map(personId => {
      const prev = before.get(personId)?.rating ?? o.startingRating
      return {
        personId,
        side,
        won: score === 1,
        kFactor: k,
        before: prev,
        delta,
        after: quantizeRating(prev + delta),
      }
    })
  }

  return [
    ...build('a', match.sideA, scoreA, expectedA, aProv, bProv),
    ...build('b', match.sideB, scoreB, expectedB, bProv, aProv),
  ]
}

/**
 * Apply a match to a ratings table.
 *
 * Ratings for other resources and for non-participants are returned untouched
 * and in their original order; players new to this resource are appended in the
 * order they appear in the match, so the result is a deterministic function of
 * the inputs. An invalid match changes nothing.
 *
 * A table carrying two rows for one (person, resource) — which dirty synced data
 * can — collapses to the FIRST, matching {@link getRating}. Keeping the last
 * instead meant the delta was computed from one row and written onto another.
 */
export function applyMatch(
  ratings: readonly Rating[],
  match: Match,
  opts?: LadderOptions,
): Rating[] {
  const changes = matchRatingChanges(ratings, match, opts)
  if (changes.length === 0) return ratings.slice()
  const o = resolveOptions(opts)

  const order: string[] = []
  const table = new Map<string, Rating>()
  for (const r of ratings) {
    const k = ratingKey(r.personId, r.resourceId)
    if (table.has(k)) continue
    order.push(k)
    table.set(k, r)
  }

  for (const c of changes) {
    const key = ratingKey(c.personId, match.resourceId)
    const prev = table.get(key) ?? newRating(c.personId, match.resourceId, opts)
    if (!table.has(key)) order.push(key)
    const matchesPlayed = prev.matchesPlayed + 1
    const currentStreak = c.won
      ? prev.currentStreak > 0
        ? prev.currentStreak + 1
        : 1
      : prev.currentStreak < 0
        ? prev.currentStreak - 1
        : -1
    table.set(key, {
      ...prev,
      rating: c.after,
      matchesPlayed,
      provisional: matchesPlayed < o.provisionalThreshold,
      wins: prev.wins + (c.won ? 1 : 0),
      losses: prev.losses + (c.won ? 0 : 1),
      currentStreak,
      bestStreak: c.won ? Math.max(prev.bestStreak, currentStreak) : prev.bestStreak,
    })
  }

  return order.map(k => table.get(k)!).filter((r): r is Rating => r !== undefined)
}

/**
 * A total order on matches that does not depend on where they sit in the array.
 * `playedAt` then id settles every pair of *distinct* matches; the content key
 * only ever decides between two rows sharing an id, which is the case the sort
 * exists to make deterministic.
 */
function compareMatches(a: Match, b: Match): number {
  return (
    a.playedAt - b.playedAt ||
    compareStrings(a.id, b.id) ||
    compareStrings(matchContentKey(a), matchContentKey(b))
  )
}

const matchContentKey = (m: Match): string =>
  [
    m.resourceId,
    m.kind,
    m.winner,
    m.scoreA,
    m.scoreB,
    m.sideA.join(','),
    m.sideB.join(','),
    m.recordedBy,
    m.verifiedBy ?? '',
  ].join('\u0000')

/**
 * Canonical match order: sorted by `playedAt` with the id as tiebreak, then
 * de-duplicated by id.
 *
 * Every derived statistic runs through this, which is what makes them
 * replay-safe: a synced log containing the same match twice, or arriving in a
 * different order on a different device, produces identical output.
 *
 * Sorting BEFORE de-duplicating is the load-bearing detail. De-duplicating first
 * keeps whichever copy the array happened to list first, so two rows sharing an
 * id but disagreeing on content — an edited match replayed from two devices —
 * resolved differently depending on arrival order, and `rebuildRatings` was not
 * in fact order-invariant. Sorting first makes the survivor a function of the
 * *set* of rows, so every device keeps the same one. Found by property test.
 */
export function normaliseMatches(matches: readonly Match[]): Match[] {
  const seen = new Set<string>()
  const out: Match[] = []
  for (const m of matches.slice().sort(compareMatches)) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    out.push(m)
  }
  return out
}

/** Replay a whole match log into a ratings table. Order- and duplicate-proof. */
export function rebuildRatings(
  matches: readonly Match[],
  opts?: LadderOptions,
  resourceId?: ResourceId,
): Rating[] {
  const log = normaliseMatches(matches).filter(
    m => (resourceId === undefined || m.resourceId === resourceId) && isValidMatch(m),
  )
  return log.reduce<Rating[]>((acc, m) => applyMatch(acc, m, opts), [])
}

// --- derived stats, straight off the match log (docs/01 §5.2) --------------

/** Which side a person played on, or null if they were not in the match. */
export function sideOf(match: Match, personId: PersonId): 'a' | 'b' | null {
  if (match.sideA.includes(personId)) return 'a'
  if (match.sideB.includes(personId)) return 'b'
  return null
}

export const matchParticipants = (match: Match): PersonId[] => [...match.sideA, ...match.sideB]

/** True/false if they played, null if they did not. */
export function didWin(match: Match, personId: PersonId): boolean | null {
  const side = sideOf(match, personId)
  return side === null ? null : side === match.winner
}

function personMatches(
  matches: readonly Match[],
  personId: PersonId,
  resourceId?: ResourceId,
): Match[] {
  return normaliseMatches(matches).filter(
    m =>
      (resourceId === undefined || m.resourceId === resourceId) &&
      isValidMatch(m) &&
      sideOf(m, personId) !== null,
  )
}

/**
 * "You are 12-4 against Omar." Counts only matches where the two were on
 * opposite sides, so a doubles pairing on the same team is not a head-to-head.
 */
export function headToHead(
  matches: readonly Match[],
  a: PersonId,
  b: PersonId,
  resourceId?: ResourceId,
): HeadToHead {
  let aWins = 0
  let bWins = 0
  for (const m of normaliseMatches(matches)) {
    if (resourceId !== undefined && m.resourceId !== resourceId) continue
    if (!isValidMatch(m)) continue
    const sa = sideOf(m, a)
    const sb = sideOf(m, b)
    if (sa === null || sb === null || sa === sb) continue
    if (m.winner === sa) aWins++
    else bWins++
  }
  return { a, b, aWins, bWins }
}

export interface Upset {
  readonly match: Match
  /** Rating points the winning side was behind by. Always > 0. */
  readonly gap: number
  readonly winnerRating: number
  readonly loserRating: number
}

function sideAverage(
  side: readonly PersonId[],
  lookup: (p: PersonId) => number,
  fallback: number,
): number {
  if (side.length === 0) return fallback
  return side.reduce((s, p) => s + lookup(p), 0) / side.length
}

/**
 * The biggest upset in the log, measured against a ratings snapshot.
 *
 * `gap` is how far the winning side was rated *below* the losing side. Only
 * positive gaps count — beating someone rated lower is not an upset. Ties break
 * on the earliest match, then id, so the answer is stable across devices.
 *
 * This scores every match against today's ratings. {@link biggestUpsetFromLog}
 * is the historically honest version, scoring each match against the ratings as
 * they stood immediately before it was played.
 */
export function biggestUpset(
  matches: readonly Match[],
  ratings: readonly Rating[],
  opts?: LadderOptions,
): Upset | null {
  const o = resolveOptions(opts)
  let best: Upset | null = null
  for (const m of normaliseMatches(matches)) {
    if (!isValidMatch(m)) continue
    const look = (p: PersonId): number =>
      ratings.find(r => r.personId === p && r.resourceId === m.resourceId)?.rating ??
      o.startingRating
    const winners = m.winner === 'a' ? m.sideA : m.sideB
    const losers = m.winner === 'a' ? m.sideB : m.sideA
    const winnerRating = sideAverage(winners, look, o.startingRating)
    const loserRating = sideAverage(losers, look, o.startingRating)
    const gap = loserRating - winnerRating
    if (gap <= 0) continue
    if (best === null || gap > best.gap) best = { match: m, gap, winnerRating, loserRating }
  }
  return best
}

/** Biggest upset scored against the ratings as they stood before each match. */
export function biggestUpsetFromLog(
  matches: readonly Match[],
  opts?: LadderOptions,
  resourceId?: ResourceId,
): Upset | null {
  const o = resolveOptions(opts)
  const log = normaliseMatches(matches).filter(
    m => (resourceId === undefined || m.resourceId === resourceId) && isValidMatch(m),
  )
  let table: Rating[] = []
  let best: Upset | null = null
  for (const m of log) {
    const look = (p: PersonId): number => getRating(table, p, m.resourceId, opts).rating
    const winners = m.winner === 'a' ? m.sideA : m.sideB
    const losers = m.winner === 'a' ? m.sideB : m.sideA
    const winnerRating = sideAverage(winners, look, o.startingRating)
    const loserRating = sideAverage(losers, look, o.startingRating)
    const gap = loserRating - winnerRating
    if (gap > 0 && (best === null || gap > best.gap)) {
      best = { match: m, gap, winnerRating, loserRating }
    }
    table = applyMatch(table, m, opts)
  }
  return best
}

export interface StreakSummary {
  readonly personId: PersonId
  /** Positive = consecutive wins, negative = consecutive losses, 0 = no matches. */
  readonly current: number
  readonly longestWin: number
  readonly longestLoss: number
  readonly wins: number
  readonly losses: number
  readonly played: number
}

/** Current and best streaks straight off the log — no extra storage. */
export function streakSummary(
  matches: readonly Match[],
  personId: PersonId,
  resourceId?: ResourceId,
): StreakSummary {
  let current = 0
  let longestWin = 0
  let longestLoss = 0
  let wins = 0
  let losses = 0
  let played = 0
  for (const m of personMatches(matches, personId, resourceId)) {
    const won = didWin(m, personId) === true
    played++
    if (won) {
      wins++
      current = current > 0 ? current + 1 : 1
      longestWin = Math.max(longestWin, current)
    } else {
      losses++
      current = current < 0 ? current - 1 : -1
      longestLoss = Math.max(longestLoss, -current)
    }
  }
  return { personId, current, longestWin, longestLoss, wins, losses, played }
}

export const longestWinStreak = (
  matches: readonly Match[],
  personId: PersonId,
  resourceId?: ResourceId,
): number => streakSummary(matches, personId, resourceId).longestWin

export const currentWinStreak = (
  matches: readonly Match[],
  personId: PersonId,
  resourceId?: ResourceId,
): number => Math.max(0, streakSummary(matches, personId, resourceId).current)

/** Everyone in the log, ranked by longest win streak. Ties break on person id. */
export function longestWinStreaks(
  matches: readonly Match[],
  resourceId?: ResourceId,
): StreakSummary[] {
  const people: PersonId[] = []
  const seen = new Set<PersonId>()
  for (const m of normaliseMatches(matches)) {
    if (resourceId !== undefined && m.resourceId !== resourceId) continue
    if (!isValidMatch(m)) continue
    for (const p of matchParticipants(m)) {
      if (!seen.has(p)) {
        seen.add(p)
        people.push(p)
      }
    }
  }
  return people
    .map(p => streakSummary(matches, p, resourceId))
    .sort(
      (x, y) =>
        y.longestWin - x.longestWin ||
        y.wins - x.wins ||
        compareStrings(x.personId, y.personId),
    )
}

export const winRate = (r: Rating): number =>
  r.matchesPlayed === 0 ? 0 : r.wins / r.matchesPlayed

/** Rating as shown on the wall. Stored ratings stay fractional so Elo stays zero-sum. */
export const displayRating = (r: Rating): number => Math.round(r.rating)

/** Standings for one resource: rating desc, then matches played, then id. */
export function leaderboard(ratings: readonly Rating[], resourceId: ResourceId): Rating[] {
  return ratings
    .filter(r => r.resourceId === resourceId)
    .slice()
    .sort(
      (a, b) =>
        b.rating - a.rating ||
        b.matchesPlayed - a.matchesPlayed ||
        compareStrings(a.personId, b.personId),
    )
}

// ---------------------------------------------------------------------------

/** Locale-independent string ordering — `localeCompare` is not device-stable. */
function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}
